"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/app/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipArrow = TooltipPrimitive.Arrow;

const TooltipContent = React.forwardRef<
   React.ElementRef<typeof TooltipPrimitive.Content>,
   React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
      showArrow?: boolean;
      maxWidth?: string;
   }
>(
   (
      {
         className,
         sideOffset = 4,
         showArrow = false,
         maxWidth = "max-w-xs",
         children,
         ...props
      },
      ref
   ) => (
      <TooltipPrimitive.Content
         ref={ref}
         sideOffset={sideOffset}
         className={cn(
            "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95",
            "whitespace-normal break-words",
            maxWidth,
            className
         )}
         {...props}
      >
         {children}
         {showArrow && <TooltipArrow className="fill-popover" />}
      </TooltipPrimitive.Content>
   )
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface CustomTooltipProps {
   children: React.ReactNode;
   text?: Exclude<React.ReactNode, boolean | null | undefined>;
   delayDuration?: number;
   position?: "top" | "right" | "bottom" | "left";
   align?: "start" | "center" | "end";
   showArrow?: boolean;
   maxWidth?: string;
}

function CustomTooltip({
   children,
   text,
   delayDuration = 700,
   position = "top",
   align = "center",
   showArrow = false,
   maxWidth = "max-w-xs"
}: CustomTooltipProps) {
   if (text == null || typeof text === "boolean") {
      return <>{children}</>;
   }

   return (
      <TooltipProvider delayDuration={delayDuration}>
         <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent
               side={position}
               align={align}
               showArrow={showArrow}
               maxWidth={maxWidth}
            >
               <div className="text-sm">{text}</div>
            </TooltipContent>
         </Tooltip>
      </TooltipProvider>
   );
}

export {
   Tooltip,
   TooltipTrigger,
   TooltipContent,
   TooltipArrow,
   TooltipProvider,
   CustomTooltip
};
